import React, { useState, useMemo, useCallback, useRef } from "react";
import {
  Dialog,
  DialogBackdrop,
  DialogPanel,
  TransitionChild,
} from "@headlessui/react";
import { Bars3Icon, XMarkIcon, PlusIcon } from "@heroicons/react/24/outline";
import { toast } from "react-toastify";

// Components
import { Logo } from "../Logo";
import { Button } from "../Button";
import { SampleDataBanner } from "../SampleDataBanner";
import { SELECTED_TYPE } from "../panels/GoalWorkflowPanel";
import { NodeSettingsPanel } from "../panels/NodeSettingsPanel";
import { ActionAnalyticsPanel } from "../panels/ActionAnalyticsPanel";
import AddStakeholderDialog from "../dialogs/AddStakeholderDialog";
import AddSegmentDialog from "../dialogs/AddSegmentDialog";
import AddActionDialog from "../dialogs/AddActionDialog";
import ConfirmationDialog from "../dialogs/ConfirmationDialog";
import {
  GoalSection,
  countRealActions,
  getSegmentActions,
} from "./GoalCardStrip";

// Services
import { APIs } from "../../services/Apis";

function classNames(...classes) {
  return classes.filter(Boolean).join(" ");
}

function countStakeholderActions(stakeholder) {
  return (stakeholder?.triggers || []).reduce(
    (total, trigger) => total + countRealActions(getSegmentActions(trigger)),
    0,
  );
}

export function OverviewTab({
  onRefresh,
  segments = [],
  segmentEdges = [],
  loading = false,
  businessUrl,
  isOnboarding = false,
  onActionOverride,
  dbActive = true,
  globalActive = true,
  displayName = "",
  businessName = "",
  refreshing = false,
  onConnectDb,
  isSampleDb = false,
}) {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const containerRef = useRef(null);

  const [activeStakeholderIndex, setActiveStakeholderIndex] = useState(0);
  const [addStakeholderOpen, setAddStakeholderOpen] = useState(false);
  const [addSegmentOpen, setAddSegmentOpen] = useState(false);

  const [deleteSegmentDialogOpen, setDeleteSegmentDialogOpen] = useState(false);
  const [segmentToDelete, setSegmentToDelete] = useState(null);
  const [isDeletingSegment, setIsDeletingSegment] = useState(false);

  const [deleteActionDialogOpen, setDeleteActionDialogOpen] = useState(false);
  const [actionToDelete, setActionToDelete] = useState(null);
  const [isDeletingAction, setIsDeletingAction] = useState(false);

  const [addActionSegment, setAddActionSegment] = useState(null);
  const [analyticsSegment, setAnalyticsSegment] = useState(null);

  // Which card currently has the settings panel open.
  const [selection, setSelection] = useState({
    segment: null,
    type: null,
    action: null,
  });

  const blockedByOnboarding = useCallback(() => {
    if (isOnboarding && onActionOverride) {
      onActionOverride();
      return true;
    }
    return false;
  }, [isOnboarding, onActionOverride]);

  const blockedByDatabase = useCallback(() => {
    if (!dbActive && !isOnboarding) {
      if (onConnectDb) onConnectDb();
      return true;
    }
    return false;
  }, [dbActive, isOnboarding, onConnectDb]);

  const handleSelectGoal = useCallback(
    (segment) => {
      if (blockedByOnboarding() || blockedByDatabase()) return;
      setSelection({ segment, type: SELECTED_TYPE.GOAL, action: null });
    },
    [blockedByOnboarding, blockedByDatabase],
  );

  const handleSelectAction = useCallback(
    (segment, action) => {
      if (blockedByOnboarding() || blockedByDatabase()) return;
      setSelection({ segment, type: SELECTED_TYPE.ACTION, action });
    },
    [blockedByOnboarding, blockedByDatabase],
  );

  const handleCloseSettings = useCallback(() => {
    setSelection({ segment: null, type: null, action: null });
  }, []);

  const handleAnalytics = useCallback(
    (segment) => {
      if (blockedByOnboarding()) return;
      setAnalyticsSegment(segment);
    },
    [blockedByOnboarding],
  );

  const handleAddAction = useCallback(
    (segment) => {
      if (blockedByOnboarding()) return;
      setAddActionSegment(segment);
    },
    [blockedByOnboarding],
  );

  const handleRequestDeleteGoal = useCallback(
    (segment) => {
      if (blockedByOnboarding()) return;
      setSegmentToDelete(segment);
      setDeleteSegmentDialogOpen(true);
    },
    [blockedByOnboarding],
  );

  const handleRequestDeleteAction = useCallback(
    (action) => {
      if (blockedByOnboarding()) return;
      setActionToDelete(action);
      setDeleteActionDialogOpen(true);
    },
    [blockedByOnboarding],
  );

  const handleStakeholderAdded = () => {
    setAddStakeholderOpen(false);
    if (onRefresh) onRefresh();
  };

  const handleSegmentAdded = () => {
    setAddSegmentOpen(false);
    if (onRefresh) onRefresh();
  };

  const handleDeleteSegment = async () => {
    if (!segmentToDelete) return;
    const segmentId = segmentToDelete.segmentId || segmentToDelete.id;
    if (!segmentId) return;

    setIsDeletingSegment(true);
    try {
      const response = await APIs.deleteSegment(segmentId);
      if (response && response.success) {
        toast.success("Goal deleted successfully");
        setDeleteSegmentDialogOpen(false);
        setSegmentToDelete(null);
        const selectedId =
          selection.segment?.segmentId || selection.segment?.id;
        if (selectedId === segmentId) {
          handleCloseSettings();
        }
        if (onRefresh) onRefresh();
      } else {
        toast.error(response?.message || "Failed to delete goal");
      }
    } catch (error) {
      console.error("Error deleting goal:", error);
      toast.error("An error occurred while deleting the goal");
    } finally {
      setIsDeletingSegment(false);
    }
  };

  const handleDeleteAction = async () => {
    if (!actionToDelete) return;
    const actionId = actionToDelete.id || actionToDelete.segmentActionId;
    if (!actionId) return;

    setIsDeletingAction(true);
    try {
      const response = await APIs.deleteSegmentAction(actionId);
      if (response?.success) {
        toast.success("Action deleted successfully!");
        setDeleteActionDialogOpen(false);
        setActionToDelete(null);
        const selectedActionId =
          selection.action?.id || selection.action?.segmentActionId;
        if (selectedActionId === actionId) {
          handleCloseSettings();
        }
        if (onRefresh) onRefresh();
      } else {
        toast.error(response?.message || "Failed to delete action");
      }
    } catch (error) {
      console.error("Error deleting action:", error);
      toast.error("Failed to delete action");
    } finally {
      setIsDeletingAction(false);
    }
  };

  const sortedSegments = useMemo(
    () =>
      [...segments].sort(
        (a, b) =>
          Number(a.id || a.segmentId || 0) - Number(b.id || b.segmentId || 0),
      ),
    [segments],
  );

  const activeStakeholder = sortedSegments[activeStakeholderIndex];
  const goals = activeStakeholder?.triggers || [];

  const selectedSegmentId =
    selection.segment?.segmentId || selection.segment?.id;
  const selectedActionId =
    selection.action?.id || selection.action?.segmentActionId;

  // Keep the settings panel bound to the freshest copy of the segment after a
  // refresh replaces the objects we handed out.
  const activeSegment = useMemo(() => {
    if (!selectedSegmentId) return null;
    for (const stakeholder of segments) {
      for (const trigger of stakeholder.triggers || []) {
        if (
          trigger.id === selectedSegmentId ||
          trigger.segmentId === selectedSegmentId
        ) {
          return trigger;
        }
      }
      if (
        stakeholder.id === selectedSegmentId ||
        stakeholder.segmentId === selectedSegmentId
      ) {
        return stakeholder;
      }
    }
    return selection.segment;
  }, [segments, selectedSegmentId, selection.segment]);

  const sidebarContent = (
    <>
      <div className="relative flex h-16 shrink-0 items-center px-4 z-0">
        <a
          href={isOnboarding ? "#" : "/dashboard"}
          className="flex items-center gap-2 hover:opacity-80 transition-opacity"
          aria-label="Home"
          onClick={(e) => {
            if (isOnboarding) e.preventDefault();
          }}
        >
          <Logo className="h-4 w-auto" />
          <span
            className="text-2xl font-semibold text-gray-900"
            style={{ fontFamily: "'Questrial', sans-serif" }}
          >
            fluvo
          </span>
        </a>
      </div>
      <nav className="relative flex flex-1 flex-col px-4 mt-4">
        <ul role="list" className="flex flex-1 flex-col gap-y-7">
          <li>
            <div className="text-xs/6 font-semibold text-gray-400">
              Stakeholders
            </div>
            <ul role="list" className="-mx-2 mt-2 space-y-1">
              {sortedSegments.map((stakeholder, index) => {
                const current = activeStakeholderIndex === index;
                const initial = stakeholder.title
                  ? stakeholder.title.substring(0, 2).toUpperCase()
                  : "??";
                const goalCount = stakeholder.triggers?.length || 0;
                return (
                  <li key={stakeholder.id || index}>
                    <button
                      onClick={() => {
                        setActiveStakeholderIndex(index);
                        setSidebarOpen(false);
                        handleCloseSettings();
                      }}
                      className={classNames(
                        current
                          ? "bg-gray-50 text-gray-900"
                          : "text-gray-700 hover:bg-gray-50 hover:text-grey-900",
                        "group flex w-full items-center gap-x-3 rounded-md px-3 py-2.5 text-sm/6 font-semibold text-left cursor-pointer",
                      )}
                    >
                      <span
                        className={classNames(
                          current
                            ? "border-gray-900 text-gray-900"
                            : "border-gray-200 text-gray-400 group-hover:border-gray-900 group-hover:text-gray-900",
                          "flex size-6 shrink-0 items-center justify-center rounded-lg border bg-white text-[0.625rem] font-medium",
                        )}
                      >
                        {initial}
                      </span>
                      <span className="truncate">{stakeholder.title}</span>
                      <span className="ml-auto shrink-0 text-[11px] font-medium text-gray-400">
                        {goalCount === 1 ? "1 goal" : `${goalCount} goals`}
                      </span>
                    </button>
                  </li>
                );
              })}
              <li>
                <button
                  onClick={() =>
                    onActionOverride
                      ? onActionOverride()
                      : setAddStakeholderOpen(true)
                  }
                  className="group flex w-full gap-x-3 rounded-md px-3 py-2.5 text-sm/6 font-semibold text-gray-700 hover:bg-gray-50 hover:text-grey-900 text-left mt-2 cursor-pointer"
                >
                  <span className="flex size-6 shrink-0 items-center justify-center rounded-lg border border-dashed border-gray-200 text-gray-400 bg-white group-hover:border-gray-900 group-hover:text-gray-900">
                    <PlusIcon className="w-4 h-4" />
                  </span>
                  <span className="truncate">Add Stakeholder</span>
                </button>
              </li>
            </ul>
          </li>

          {!isOnboarding && (
            <li className="-mx-4 mt-auto flex flex-col">
              {!dbActive && (
                <a
                  href="/settings?tab=database-connection"
                  className="mx-4 mb-2 flex items-center gap-2 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2.5 text-xs font-medium text-amber-800 hover:bg-amber-100 transition-colors"
                >
                  <svg
                    className="size-4 shrink-0 text-amber-500"
                    fill="none"
                    viewBox="0 0 24 24"
                    strokeWidth="1.5"
                    stroke="currentColor"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      d="M12 9v3.75m-9.303 3.376c-.866 1.5.217 3.374 1.948 3.374h14.71c1.73 0 2.813-1.874 1.948-3.374L13.949 3.378c-.866-1.5-3.032-1.5-3.898 0L2.697 16.126ZM12 15.75h.007v.008H12v-.008Z"
                    />
                  </svg>
                  Connect your customer data
                </a>
              )}
              {!globalActive && (
                <a
                  href="/settings#workflow-activation"
                  className="mx-4 mb-2 flex items-center gap-2 rounded-lg border border-red-200 bg-red-50 px-3 py-2.5 text-xs font-medium text-red-800 hover:bg-red-100 transition-colors"
                >
                  <svg
                    className="size-4 shrink-0 text-red-500"
                    fill="none"
                    viewBox="0 0 24 24"
                    strokeWidth="1.5"
                    stroke="currentColor"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      d="M14.25 9v6m-4.5 0V9M21 12a9 9 0 1 1-18 0 9 9 0 0 1 18 0Z"
                    />
                  </svg>
                  All workflows are paused. Go to settings to reactivate.
                </a>
              )}
              <a
                href="/settings?tab=brand-kit"
                className="flex items-center gap-x-4 px-4 py-3 text-sm/6 font-semibold text-gray-900 hover:bg-gray-50"
              >
                <div className="flex size-8 shrink-0 items-center justify-center rounded-full border border-gray-300 bg-white">
                  <svg
                    className="size-5 text-gray-500"
                    fill="none"
                    viewBox="0 0 24 24"
                    strokeWidth="1.5"
                    stroke="currentColor"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      d="M9.813 15.904L9 18.75l-.813-2.846a4.5 4.5 0 00-3.09-3.09L2.25 12l2.846-.813a4.5 4.5 0 003.09-3.09L9 5.25l.813 2.846a4.5 4.5 0 003.09 3.09L15.75 12l-2.846.813a4.5 4.5 0 00-3.09 3.09zM18.259 8.715L18 9.75l-.259-1.035a3.375 3.375 0 00-2.455-2.456L14.25 6l1.036-.259a3.375 3.375 0 002.455-2.456L18 2.25l.259 1.035a3.375 3.375 0 002.456 2.456L21.75 6l-1.035.259a3.375 3.375 0 00-2.456 2.456zM16.894 20.567L16.5 21.75l-.394-1.183a2.25 2.25 0 00-1.423-1.423L13.5 18.75l1.183-.394a2.25 2.25 0 001.423-1.423l.394-1.183.394 1.183a2.25 2.25 0 001.423 1.423l1.183.394-1.183.394a2.25 2.25 0 00-1.423 1.423z"
                    />
                  </svg>
                </div>
                <span aria-hidden="true">Brand Kit</span>
              </a>
              <a
                href="/settings"
                className="flex items-center gap-x-4 px-4 py-3 text-sm/6 font-semibold text-gray-900 hover:bg-gray-50 border-t border-gray-100"
              >
                <div className="flex size-8 shrink-0 items-center justify-center rounded-full bg-primary/20 text-gray-900 font-bold text-xs">
                  {displayName
                    ? displayName.substring(0, 2).toUpperCase()
                    : "U"}
                </div>
                <div className="flex flex-col">
                  <span aria-hidden="true" className="truncate">
                    {displayName || "Settings"}
                  </span>
                  {businessName && (
                    <span className="text-xs font-normal text-gray-500 truncate">
                      {businessName}
                    </span>
                  )}
                </div>
              </a>
            </li>
          )}
        </ul>
      </nav>
    </>
  );

  const showSampleBanner = isSampleDb && !isOnboarding;

  return (
    <>
      <div className="h-full w-full bg-white relative">
        {showSampleBanner && <SampleDataBanner />}
        <Dialog
          open={sidebarOpen}
          onClose={setSidebarOpen}
          className="relative z-[100] lg:hidden"
        >
          <DialogBackdrop
            transition
            className="fixed inset-0 bg-gray-900/80 transition-opacity duration-300 ease-linear data-closed:opacity-0"
          />

          <div className="fixed inset-0 flex">
            <DialogPanel
              transition
              className="relative mr-16 flex w-full max-w-xs flex-1 transform transition duration-300 ease-in-out data-closed:-translate-x-full"
            >
              <TransitionChild>
                <div className="absolute top-0 left-full flex w-16 justify-center pt-5 duration-300 ease-in-out data-closed:opacity-0">
                  <button
                    type="button"
                    onClick={() => setSidebarOpen(false)}
                    className="-m-2.5 p-2.5"
                  >
                    <span className="sr-only">Close sidebar</span>
                    <XMarkIcon
                      aria-hidden="true"
                      className="size-6 text-white"
                    />
                  </button>
                </div>
              </TransitionChild>

              <div className="relative flex grow flex-col gap-y-5 overflow-y-auto bg-white pb-2">
                {sidebarContent}
              </div>
            </DialogPanel>
          </div>
        </Dialog>

        {/* Static sidebar for desktop */}
        <div
          className={classNames(
            "hidden lg:fixed lg:bottom-0 lg:z-40 lg:flex lg:w-72 lg:flex-col z-0",
            showSampleBanner ? "lg:top-9" : "lg:top-0",
          )}
        >
          <div className="relative flex grow flex-col gap-y-5 overflow-y-auto border-r border-gray-200 bg-white">
            {sidebarContent}
          </div>
        </div>

        <div
          className={classNames(
            "sticky z-40 flex items-center gap-x-6 bg-white px-4 py-4 shadow-xs sm:px-6 lg:hidden border-b border-gray-200",
            showSampleBanner ? "top-9" : "top-0",
          )}
        >
          <button
            type="button"
            onClick={() => setSidebarOpen(true)}
            className="relative -m-2.5 p-2.5 text-gray-700 lg:hidden"
          >
            <span className="sr-only">Open sidebar</span>
            <Bars3Icon aria-hidden="true" className="size-6" />
          </button>
          <div className="relative flex-1 text-sm/6 font-semibold text-gray-900">
            <Logo className="h-6 w-auto inline-block mr-2" />
            fluvo
          </div>
          {!isOnboarding && (
            <a href="/settings" className="relative">
              <span className="sr-only">Your profile</span>
              <div className="flex size-8 shrink-0 items-center justify-center rounded-full bg-primary/20 text-gray-900 font-bold text-xs">
                {displayName ? displayName.substring(0, 2).toUpperCase() : "U"}
              </div>
            </a>
          )}
        </div>

        <div
          ref={containerRef}
          className="lg:pl-72"
          style={{
            display: "flex",
            flexDirection: "column",
            height: showSampleBanner ? "calc(100vh - 36px)" : "100vh",
            minHeight: 0,
          }}
        >
          {activeStakeholder ? (
            <>
              <div className="flex flex-none items-center justify-between gap-4 border-b border-gray-200 px-4 py-4 sm:px-6 lg:px-8">
                <div className="min-w-0">
                  <h2 className="flex items-center gap-2 text-lg font-semibold text-gray-900">
                    Goals ({goals.length})
                    {refreshing && (
                      <svg
                        className="animate-spin h-4 w-4 text-gray-400"
                        xmlns="http://www.w3.org/2000/svg"
                        fill="none"
                        viewBox="0 0 24 24"
                      >
                        <circle
                          className="opacity-25"
                          cx="12"
                          cy="12"
                          r="10"
                          stroke="currentColor"
                          strokeWidth="4"
                        />
                        <path
                          className="opacity-75"
                          fill="currentColor"
                          d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"
                        />
                      </svg>
                    )}
                  </h2>
                  <div className="mt-0.5 truncate text-xs text-gray-500">
                    {activeStakeholder.title}
                    {" · "}
                    {countStakeholderActions(activeStakeholder) === 1
                      ? "1 action"
                      : `${countStakeholderActions(activeStakeholder)} actions`}
                  </div>
                </div>
                <Button
                  variant="solid"
                  color="primary"
                  size="sm"
                  className="flex shrink-0 items-center gap-2"
                  onClick={() =>
                    onActionOverride
                      ? onActionOverride()
                      : setAddSegmentOpen(true)
                  }
                >
                  <span>+</span>
                  <span>New Goal</span>
                </Button>
              </div>

              <main className="flex-1 overflow-y-auto bg-white">
                {goals.length > 0 ? (
                  <div className="flex flex-col gap-7 py-6 pb-16">
                    {goals.map((segment, index) => {
                      const segmentId = segment.segmentId || segment.id;
                      return (
                        <GoalSection
                          key={segmentId || index}
                          segment={segment}
                          isSelectedSegment={
                            selectedSegmentId != null &&
                            String(selectedSegmentId) === String(segmentId)
                          }
                          selectedType={selection.type}
                          selectedActionId={selectedActionId}
                          isOnboarding={isOnboarding}
                          onActionOverride={onActionOverride}
                          onSelectGoal={handleSelectGoal}
                          onSelectAction={handleSelectAction}
                          onAnalytics={handleAnalytics}
                          onAddAction={handleAddAction}
                          onDeleteGoal={handleRequestDeleteGoal}
                          onDeleteAction={handleRequestDeleteAction}
                          onRefresh={onRefresh}
                        />
                      );
                    })}
                  </div>
                ) : (
                  <div className="py-16 text-center">
                    <p className="text-gray-500">
                      No goals yet for {activeStakeholder.title}.
                    </p>
                    <Button
                      variant="solid"
                      color="primary"
                      className="mt-4"
                      onClick={() =>
                        onActionOverride
                          ? onActionOverride()
                          : setAddSegmentOpen(true)
                      }
                    >
                      New Goal
                    </Button>
                  </div>
                )}
              </main>
            </>
          ) : (
            <div className="px-4 py-8 w-full">
              <div className="text-center py-12">
                <p className="text-gray-500">No journeys available</p>
                <Button
                  variant="solid"
                  color="primary"
                  className="mt-4"
                  onClick={() =>
                    onActionOverride
                      ? onActionOverride()
                      : setAddStakeholderOpen(true)
                  }
                >
                  Add Stakeholder
                </Button>
              </div>
            </div>
          )}
        </div>
      </div>

      <NodeSettingsPanel
        isOpen={selection.type !== null}
        onClose={handleCloseSettings}
        selectedType={selection.type}
        selectedAction={selection.action}
        segment={activeSegment}
        onRefresh={onRefresh}
        isOnboarding={isOnboarding}
      />

      <ActionAnalyticsPanel
        isOpen={analyticsSegment !== null}
        onClose={() => setAnalyticsSegment(null)}
        segmentId={analyticsSegment?.id || analyticsSegment?.segmentId}
      />

      {!isOnboarding && (
        <>
          <AddStakeholderDialog
            open={addStakeholderOpen}
            setOpen={setAddStakeholderOpen}
            businessUrl={businessUrl}
            onSuccess={handleStakeholderAdded}
          />

          <AddSegmentDialog
            open={addSegmentOpen}
            setOpen={setAddSegmentOpen}
            stakeholderId={activeStakeholder?.id}
            stakeholderName={activeStakeholder?.title}
            onSuccess={handleSegmentAdded}
          />

          <AddActionDialog
            open={addActionSegment !== null}
            setOpen={(open) => {
              if (!open) setAddActionSegment(null);
            }}
            segmentId={addActionSegment?.id || addActionSegment?.segmentId}
            onSuccess={onRefresh}
          />
        </>
      )}

      <ConfirmationDialog
        open={deleteSegmentDialogOpen}
        setOpen={setDeleteSegmentDialogOpen}
        title="Delete Goal"
        description="Are you sure you want to delete this goal? This action cannot be undone."
        confirmationButton={
          <button
            type="button"
            className="inline-flex w-full justify-center rounded-md bg-red-600 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-red-500 sm:ml-3 sm:w-auto"
            onClick={handleDeleteSegment}
            disabled={isDeletingSegment}
          >
            {isDeletingSegment ? "Deleting..." : "Delete"}
          </button>
        }
        cancelButton={
          <button
            type="button"
            className="mt-3 inline-flex w-full justify-center rounded-md bg-white px-3 py-2 text-sm font-semibold text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50 sm:mt-0 sm:w-auto"
            onClick={() => setDeleteSegmentDialogOpen(false)}
            disabled={isDeletingSegment}
          >
            Cancel
          </button>
        }
      />

      <ConfirmationDialog
        open={deleteActionDialogOpen}
        setOpen={setDeleteActionDialogOpen}
        title="Delete Action"
        description="Are you sure you want to delete this action? This action cannot be undone."
        confirmationButton={
          <button
            type="button"
            className="inline-flex w-full justify-center rounded-md bg-red-600 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-red-500 sm:ml-3 sm:w-auto"
            onClick={handleDeleteAction}
            disabled={isDeletingAction}
          >
            {isDeletingAction ? "Deleting..." : "Delete"}
          </button>
        }
        cancelButton={
          <button
            type="button"
            className="mt-3 inline-flex w-full justify-center rounded-md bg-white px-3 py-2 text-sm font-semibold text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50 sm:mt-0 sm:w-auto"
            onClick={() => setDeleteActionDialogOpen(false)}
            disabled={isDeletingAction}
          >
            Cancel
          </button>
        }
      />
    </>
  );
}
